/**
 * 报名页面 - 支持查询和修改
 * 功能：
 * 1. 查询入口：通过手机号+姓名查询已有报名（弹窗形式）
 * 2. 新建报名：填写表单提交
 * 3. 修改报名：审核中状态可修改
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitRegistration, login, queryRegistration, updateRegistration } from '../services/api';
import { useAuthStore, useRegistrationStore } from '../stores';
import { useToast } from '../components/Toast';
import { validateForm, hasErrors, formValidationRules, validateField, validators } from '../utils/validators';
import { FormData as RegistrationFormData, Registration } from '../types';
import { competitionConfig } from '../config';

type PageMode = 'form' | 'confirm' | 'edit';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { setAuth, isAuthenticated } = useAuthStore();
  const { setRegistration, setFormData: saveFormData } = useRegistrationStore();
  const toast = useToast();

  // 页面模式（默认直接进入表单）
  const [mode, setMode] = useState<PageMode>('form');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  // 查询弹窗状态
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [queryPhone, setQueryPhone] = useState('');
  const [queryName, setQueryName] = useState('');

  // 已有报名信息
  const [existingRegistration, setExistingRegistration] = useState<Registration | null>(null);

  // 报名表单
  const [formData, setFormData] = useState<RegistrationFormData>({
    studentName: '',
    studentSchool: '',
    studentLevel: '小学',
    phone: '',
    parentName: '',
    mentorName: '',
    mentorPhone: '',
    teamMembers: '',
  });

  // 查询报名
  const handleQuery = async () => {
    // 验证
    if (!validators.phone(queryPhone)) {
      toast.error('请输入正确的手机号');
      return;
    }
    if (!validators.name(queryName)) {
      toast.error('请输入正确的学生姓名');
      return;
    }

    setIsLoading(true);
    try {
      const result = await queryRegistration(queryPhone, queryName);

      if (result.code === 0 && result.data) {
        // 找到报名记录
        setExistingRegistration(result.data);
        // 填充表单以便修改
        setFormData({
          studentName: result.data.studentName,
          studentSchool: result.data.studentSchool,
          studentLevel: result.data.studentLevel,
          phone: result.data.phone,
          parentName: result.data.parentName,
          mentorName: result.data.mentorName,
          mentorPhone: result.data.mentorPhone,
          teamMembers: result.data.teamMembers || '',
        });
        setShowQueryModal(false);
        setMode('edit');
        toast.success('已找到您的报名记录');
      } else if (result.code === 40401) {
        // 未报名，提示后保持在表单页
        toast.info('未找到报名记录，请填写报名表');
        setFormData(prev => ({
          ...prev,
          studentName: queryName,
          phone: queryPhone,
        }));
        setShowQueryModal(false);
      } else {
        throw new Error(result.message || '查询失败');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '查询失败');
    } finally {
      setIsLoading(false);
    }
  };

  // 表单变更
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    const rule = formValidationRules[name];
    if (rule) {
      const error = validateField(value, rule);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const rule = formValidationRules[name];
    if (rule) {
      const error = validateField(value, rule);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  // 提交表单进入确认
  const handleInitialSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm(formData as unknown as Record<string, string>);
    setErrors(validationErrors);

    if (hasErrors(validationErrors)) {
      toast.error('请检查表单填写是否正确');
      return;
    }

    setMode('confirm');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 最终提交（新建或修改）
  const handleFinalConfirm = async () => {
    setIsLoading(true);

    try {
      // 登录
      if (!isAuthenticated) {
        const loginResult = await login(formData.phone, formData.studentName);
        if (loginResult.code !== 0 || !loginResult.data) {
          // 提供更友好的错误提示
          if (loginResult.code === 40101) {
            throw new Error('该手机号已被其他学生注册，请确认手机号是否正确，或使用其他手机号');
          }
          throw new Error(loginResult.message || '登录失败');
        }
        setAuth(loginResult.data.token, loginResult.data.user);
      }

      // 保存表单数据
      saveFormData(formData);

      if (existingRegistration) {
        // 修改模式
        const result = await updateRegistration(existingRegistration.id, formData);
        if (result.code !== 0 || !result.data) {
          throw new Error(result.message || '修改失败');
        }
        setRegistration({
          ...formData,
          id: result.data.id,
          status: result.data.status,
          statusText: result.data.statusText,
          createdAt: result.data.createdAt,
          updatedAt: result.data.updatedAt,
        });
        toast.success('修改成功！');
      } else {
        // 新建模式
        const result = await submitRegistration(formData);
        if (result.code !== 0 || !result.data) {
          throw new Error(result.message || '报名失败');
        }
        setRegistration({
          ...formData,
          id: result.data.id,
          status: result.data.status,
          statusText: result.data.statusText,
          createdAt: result.data.createdAt,
          updatedAt: result.data.createdAt,
        });
        toast.success('报名成功！');
      }

      navigate('/status');

    } catch (error) {
      toast.error(error instanceof Error ? error.message : '提交失败，请稍后重试');
      setIsLoading(false);
    }
  };

  const inputClass = (fieldName: string) => `
    w-full px-4 py-3 rounded-xl border transition-all outline-none bg-slate-50/50
    ${errors[fieldName]
      ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200'
      : 'border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
    }
  `;
  const labelClass = "block text-sm font-semibold text-slate-700 mb-1.5";

  // ========== 查询弹窗（内联JSX，避免重新创建导致焦点丢失） ==========
  const queryModal = showQueryModal && (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in zoom-in duration-200">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-5 text-white rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">🔍 查询已有报名</h2>
            <button
              onClick={() => setShowQueryModal(false)}
              className="text-white/80 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-emerald-100 text-sm mt-1">输入报名时使用的手机号和姓名</p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className={labelClass}>联系电话</label>
            <input
              type="tel"
              value={queryPhone}
              onChange={(e) => setQueryPhone(e.target.value)}
              placeholder="请输入手机号"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none"
            />
          </div>
          <div>
            <label className={labelClass}>学生姓名</label>
            <input
              value={queryName}
              onChange={(e) => setQueryName(e.target.value)}
              placeholder="请输入学生姓名"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowQueryModal(false)}
              className="flex-1 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-50 transition-all border border-slate-200"
            >
              取消
            </button>
            <button
              onClick={handleQuery}
              disabled={isLoading}
              className="flex-1 py-3 rounded-xl font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-all disabled:bg-slate-400"
            >
              {isLoading ? '查询中...' : '查询'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );


  // ========== 编辑模式（显示已有信息） ==========
  if (mode === 'edit' && existingRegistration) {
    const canEdit = existingRegistration.status === 'pending';

    return (
      <div className="max-w-xl mx-auto px-4 py-10 animate-in slide-in-from-right duration-500">
        <toast.ToastContainer />
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
          <div className="bg-slate-900 p-8 text-white">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <span className="text-emerald-400">✓</span> 报名信息
              </h1>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${existingRegistration.status === 'pending' ? 'bg-amber-500' :
                existingRegistration.status === 'approved' ? 'bg-emerald-500' : 'bg-red-500'
                }`}>
                {existingRegistration.statusText}
              </span>
            </div>
            <p className="text-slate-400 text-sm">
              报名编号：<span className="font-mono text-blue-400">{existingRegistration.id}</span>
            </p>
          </div>

          <div className="p-8 space-y-6">
            {!canEdit && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
                ⚠️ 当前状态为「{existingRegistration.statusText}」，无法修改报名信息。
              </div>
            )}

            <div className="space-y-4">
              <ConfirmItem label="学生姓名" value={existingRegistration.studentName} />
              <ConfirmItem label="学段选择" value={existingRegistration.studentLevel} />
              <ConfirmItem label="学生学校" value={existingRegistration.studentSchool} />
              <ConfirmItem label="家长姓名" value={existingRegistration.parentName} />
              <ConfirmItem label="联系电话" value={existingRegistration.phone} />
              <div className="h-px bg-slate-100"></div>
              <ConfirmItem label="指导教师" value={existingRegistration.mentorName} />
              <ConfirmItem label="教师电话" value={existingRegistration.mentorPhone} />
              {existingRegistration.teamMembers && (
                <ConfirmItem label="其他成员" value={existingRegistration.teamMembers} />
              )}
            </div>

            <div className="flex flex-col gap-3 pt-4">
              {canEdit && (
                <button
                  onClick={() => setMode('form')}
                  className="w-full py-4 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 active:scale-[0.98]"
                >
                  修改报名信息
                </button>
              )}
              <button
                onClick={() => navigate('/')}
                className="w-full py-4 rounded-xl font-bold text-slate-500 hover:bg-slate-50 transition-all"
              >
                返回首页
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== 确认页面 ==========
  if (mode === 'confirm') {
    return (
      <div className="max-w-xl mx-auto px-4 py-10 animate-in slide-in-from-right duration-500">
        <toast.ToastContainer />
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
          <div className="bg-slate-900 p-8 text-white">
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <span className="text-blue-400">●</span>
              {existingRegistration ? '确认修改信息' : '请核对报名信息'}
            </h1>
            <p className="text-slate-400 text-sm">
              {existingRegistration ? '确认后将更新您的报名信息。' : '提交后将进入审核流程，请确保所有信息真实准确。'}
            </p>
          </div>

          <div className="p-8 space-y-6">
            <div className="grid grid-cols-1 gap-y-4">
              <ConfirmItem label="学生姓名" value={formData.studentName} />
              <ConfirmItem label="学段选择" value={formData.studentLevel} />
              <ConfirmItem label="学生学校" value={formData.studentSchool} />
              <ConfirmItem label="家长姓名" value={formData.parentName} />
              <ConfirmItem label="联系电话" value={formData.phone} />
              <div className="h-px bg-slate-100 my-2"></div>
              <ConfirmItem label="指导教师" value={formData.mentorName} />
              <ConfirmItem label="教师电话" value={formData.mentorPhone} />
              {formData.teamMembers && (
                <ConfirmItem label="其他成员" value={formData.teamMembers} />
              )}
            </div>

            <div className="flex flex-col gap-3 pt-6">
              <button
                onClick={handleFinalConfirm}
                disabled={isLoading}
                className={`w-full py-4 rounded-xl font-bold text-white transition-all shadow-lg shadow-blue-200 ${isLoading
                  ? 'bg-slate-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98]'
                  }`}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    正在提交...
                  </span>
                ) : existingRegistration ? '确认修改' : '确认无误，正式提交'}
              </button>
              <button
                onClick={() => setMode('form')}
                disabled={isLoading}
                className="w-full py-4 rounded-xl font-bold text-slate-500 hover:bg-slate-50 transition-all"
              >
                返回修改
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== 表单页面 ==========
  return (
    <div className="max-w-xl mx-auto px-4 py-10 animate-in fade-in duration-500">
      <toast.ToastContainer />
      {queryModal}

      {/* 已报名查询入口 - 小按钮 */}
      {!existingRegistration && (
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowQueryModal(true)}
            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 hover:underline"
          >
            <span>🔍</span> 已报名？查询/修改
          </button>
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
        <div className="gradient-blue p-8 text-white relative overflow-hidden">
          <div className="relative z-10 text-center">
            <h1 className="text-2xl font-bold mb-2">
              {existingRegistration ? '修改报名信息' : '赛事报名入口'}
            </h1>
            <p className="text-blue-100 text-sm">
              {existingRegistration
                ? `报名编号: ${existingRegistration.id}`
                : '请认真填写您的参赛信息，资料将作为获奖依据。'
              }
            </p>
            {!existingRegistration && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500 text-white text-[10px] font-bold uppercase tracking-wider animate-pulse">
                报名截止：{new Date(competitionConfig.registrationDeadline).toLocaleDateString('zh-CN')}
              </div>
            )}
          </div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-400/20 rounded-full -ml-12 -mb-12 blur-lg"></div>
        </div>

        <form onSubmit={handleInitialSubmit} className="p-8 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>学生姓名</label>
              <input
                name="studentName"
                value={formData.studentName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="请输入"
                className={inputClass('studentName')}
                disabled={!!existingRegistration}
              />
              {errors.studentName && (
                <p className="text-red-500 text-xs mt-1">{errors.studentName}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>学段选择</label>
              <select
                name="studentLevel"
                value={formData.studentLevel}
                onChange={handleChange}
                className={inputClass('studentLevel')}
              >
                {competitionConfig.studentLevels.map(level => (
                  <option key={level}>{level}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 其他团队成员备注 */}
          <div>
            <label className={labelClass}>其他团队成员（可选）</label>
            <textarea
              name="teamMembers"
              value={formData.teamMembers || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, teamMembers: e.target.value }))}
              placeholder="如果有其他团队成员，请在此处列出"
              rows={2}
              maxLength={200}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none bg-slate-50/50 resize-none"
            />
            <p className="text-slate-400 text-xs mt-1">最多 200 字符</p>
          </div>

          <div>
            <label className={labelClass}>学生学校</label>
            <input
              name="studentSchool"
              value={formData.studentSchool}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="请输入所在学校全称"
              className={inputClass('studentSchool')}
            />
            {errors.studentSchool && (
              <p className="text-red-500 text-xs mt-1">{errors.studentSchool}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>家长姓名</label>
              <input
                name="parentName"
                value={formData.parentName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="请输入"
                className={inputClass('parentName')}
              />
              {errors.parentName && (
                <p className="text-red-500 text-xs mt-1">{errors.parentName}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>联系电话</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="手机号码"
                className={inputClass('phone')}
                disabled={!!existingRegistration}
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-4">指导教师信息</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>教师姓名</label>
                <input
                  name="mentorName"
                  value={formData.mentorName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="请输入"
                  className={inputClass('mentorName')}
                />
                {errors.mentorName && (
                  <p className="text-red-500 text-xs mt-1">{errors.mentorName}</p>
                )}
              </div>
              <div>
                <label className={labelClass}>教师电话</label>
                <input
                  type="tel"
                  name="mentorPhone"
                  value={formData.mentorPhone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="手机号码"
                  className={inputClass('mentorPhone')}
                />
                {errors.mentorPhone && (
                  <p className="text-red-500 text-xs mt-1">{errors.mentorPhone}</p>
                )}
              </div>
            </div>
          </div>

          <div className="pt-4 flex flex-col gap-3">
            <button
              type="submit"
              className="w-full py-4 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all shadow-lg shadow-blue-200 active:scale-[0.98]"
            >
              下一步：确认信息
            </button>
            {existingRegistration && (
              <button
                type="button"
                onClick={() => {
                  setMode('edit');
                }}
                className="w-full py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-50 transition-all"
              >
                返回查看
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfirmItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 py-2 border-b border-slate-50 last:border-0">
    <span className="text-slate-400 text-sm">{label}</span>
    <span className="text-slate-800 font-bold">{value || '未填写'}</span>
  </div>
);

export default Register;
